<template>
  <div class="shopee-report-page">
    <KTCodePreview :title="'Báo cáo — Hàng cần soạn (RTS / Retry ship)'">
      <template #preview>
        <b-card class="mb-3 shopee-report-filter-card">
          <h6 class="text-muted mb-3">Bộ lọc</h6>
          <b-row align-v="start" class="shopee-report-filter-row mb-0">
            <b-col cols="12" md="4">
              <b-form-group label="Từ ngày" label-for="pl-df">
                <b-form-input id="pl-df" v-model="dateFrom" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4">
              <b-form-group label="Đến ngày" label-for="pl-dt">
                <b-form-input id="pl-dt" v-model="dateTo" type="date" size="sm" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="shopee-report-select-conn-col">
              <b-form-group label="Gian hàng" label-for="pl-conn">
                <b-form-select
                  id="pl-conn"
                  v-model="selectedConnectionId"
                  :options="shopOptions"
                  size="sm"
                  :disabled="loadingConnections || connections.length <= 1"
                >
                  <template #first>
                    <b-form-select-option :value="null">
                      {{ connections.length === 0 ? '(Chưa có kết nối)' : 'Tất cả kết nối' }}
                    </b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="shopee-report-filter-actions">
            <b-button variant="primary" size="sm" :disabled="!canSubmit" @click="fetchReport">
              <b-spinner v-if="loading" small class="mr-1"></b-spinner>
              Tải dữ liệu
            </b-button>
          </div>
        </b-card>
        <b-alert v-if="metaNote" variant="secondary" show class="small">{{ metaNote }}</b-alert>
        <b-alert v-if="error" variant="danger" show>{{ error }}</b-alert>
        <b-table
          v-if="items.length > 0"
          striped
          hover
          responsive
          :busy="loading"
          :fields="fields"
          :items="items"
          class="mt-3"
          show-empty
        />
        <p v-if="!loading && !error && fetched && items.length === 0" class="text-muted mt-3 mb-0">
          Không có dòng phù hợp.
        </p>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
  import KTCodePreview from '@/view/content/CodePreview.vue'
  import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
  import { RepositoryFactory } from '@/repositories/RepositoryFactory'

  const shopeeAdminRepo = RepositoryFactory.get('shopeeAdmin')
  const platformRepo = RepositoryFactory.get('platformIntegration')

  export default {
    name: 'ShopeeReportPickList',
    components: { KTCodePreview },
    data() {
      return {
        dateFrom: '',
        dateTo: '',
        loading: false,
        loadingConnections: false,
        connections: [],
        selectedConnectionId: null,
        error: null,
        meta: null,
        items: [],
        fetched: false,
        fields: [
          { key: 'productCode', label: 'Mã SP' },
          { key: 'productName', label: 'Tên hàng' },
          { key: 'quantity', label: 'SL cần soạn', class: 'text-right' },
        ],
      }
    },
    computed: {
      shopOptions() {
        return this.connections.map((c) => ({ value: c.id, text: c.shopName || `Shop #${c.id}` }))
      },
      canSubmit() {
        return !!(this.dateFrom && this.dateTo && !this.loading)
      },
      metaNote() {
        if (!this.meta) return ''
        const p = [`Kỳ ${this.meta.dateFrom || ''} → ${this.meta.dateTo || ''}`]
        if (this.meta.timezone) {
          p.push(this.meta.timezone)
        }
        return p.filter(Boolean).join(' · ')
      },
    },
    async created() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán', route: '/platform-integration/orders' },
        { title: 'Báo cáo', route: '/platform-integration/reports/pick-list' },
        { title: 'Pick-list RTS' },
      ])
      this.initDates()
      await this.fetchConnections()
      await this.fetchReport()
    },
    methods: {
      initDates() {
        const d = new Date()
        const to = d.toISOString().slice(0, 10)
        d.setDate(d.getDate() - 29)
        const from = d.toISOString().slice(0, 10)
        this.dateTo = to
        this.dateFrom = from
      },
      async fetchConnections() {
        try {
          this.loadingConnections = true
          const res = await platformRepo.getConfigs({ platform: 'shopee' })
          this.connections = (res.items || []).filter((c) => c.status === 'connected')
          if (this.connections.length === 1 && this.selectedConnectionId == null) {
            this.selectedConnectionId = this.connections[0].id
          }
        } catch (e) {
          console.error(e)
          this.error = 'Không tải được danh sách kết nối'
        } finally {
          this.loadingConnections = false
        }
      },
      async fetchReport() {
        this.error = null
        if (!this.dateFrom || !this.dateTo) {
          this.error = 'Vui lòng chọn đủ từ ngày và đến ngày (YYYY-MM-DD).'
          return
        }
        try {
          this.loading = true
          this.fetched = true
          const params = { dateFrom: this.dateFrom, dateTo: this.dateTo }
          if (this.selectedConnectionId != null) {
            params.connectionId = this.selectedConnectionId
          }
          const raw = await shopeeAdminRepo.getReportPickList(params)
          this.meta = raw?.meta ?? null
          this.items = Array.isArray(raw?.items) ? raw.items : []
        } catch (e) {
          console.error(e)
          this.items = []
          this.meta = null
          this.error =
            e.response?.data?.error ||
            e.response?.data?.message ||
            'Không tải được báo cáo.'
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import './shopee-report-filters.scss';
</style>
